import {
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonContent,
    IonHeader,
    IonIcon,
    IonImg,
    IonItem,
    IonItemDivider,
    IonLabel,
    IonList,
    IonModal,
    IonSelectOption,
    IonText,
    IonToolbar,
    useIonActionSheet,
    useIonToast,
} from '@ionic/react';
import { Trans, t } from '@lingui/macro';
import classNames from 'classnames';
import { Form, Formik, FormikHelpers } from 'formik';
import { hammerOutline } from 'ionicons/icons';
import { useAtom, useSetAtom } from 'jotai';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import * as Yup from 'yup';

import LoadableContent from '../../../components/content/LoadableContent';
import DefaultInput from '../../../components/forms/defaultinput/DefaultInput';
import DefaultSelect from '../../../components/forms/defaultselect/DefaultSelect';
import DefaultTextarea from '../../../components/forms/defaulttextarea/DefaultTextarea';
import LoadingButton from '../../../components/loadingbutton/LoadingButton';
import InterventionPage from '../../../components/page/InterventionPage';
import PhotoUpload from '../../../components/photoUpload/PhotoUpload';
import { TransServerMessage } from '../../../components/translations/TransServerMessage';
import { IRoomWithDevices } from '../../../interfaces/IOdooRoomsResponse';
import { IInstallationRoomRouteProps } from '../../../interfaces/IRoute';
import {
    accessTokenAtom,
    deepomaticAtom,
    devicesListLoadableAtom,
    installationWireRoomsLoadableAtom,
    photosUploadedAtoms,
    roomsListLoadableAtom,
    updateDBStateAtom,
} from '../../../store/Atoms';
import {
    createUpdateRoomRequest,
    deleteRoomRequest,
} from '../../../store/Requests';
import { THomeAssetData } from '../../../types/HomeAssetData';
import { ImageCategories } from '../../../types/ImageInfo';
import { NoAnimationBuilder } from '../../../utils/animations';
import { ODOO_INST_TYPE } from '../../../utils/constants';
import { useCustomRouter } from '../../../utils/hooks/useCustomRouter';
import usePhotosUploaded from '../../../utils/hooks/usePhotosUploaded';
import classes from './Rooms.module.css';

type RoomsFormValues = {
    name: string;
    type: number;
    insulationType: string;
    glazingType: string;
    interventionReasonCodes: string[];
    installerComment: string;
};

const MIN_ROOM_NAME_LENGTH = 3;
const RoomsSchema = (isRepair: boolean = false) =>
    Yup.object().shape({
        name: Yup.string()
            .min(
                MIN_ROOM_NAME_LENGTH,
                t`Room name is too short! Min ${MIN_ROOM_NAME_LENGTH} chars`
            )
            .required(t`Room name is required`),
        type: Yup.number().min(1, t`Please select an option`),
        insulationType: Yup.string().required(t`Please select an option`),
        glazingType: Yup.string().required(t`Please select an option`),
        interventionReasonCodes: isRepair
            ? Yup.array()
                  .of(Yup.string())
                  .min(1, 'Please select an option/s')
                  .test(
                      'RIR_0010',
                      t`"No intervention needed" cannot be selected with other options`,
                      (values) => {
                          if (
                              values &&
                              values.includes('RIR_0010') &&
                              values.length > 1
                          ) {
                              return false;
                          }
                          return true;
                      }
                  )
            : Yup.array().of(Yup.string()),
        installerComment: isRepair
            ? Yup.string().when('interventionReasonCodes', {
                  is: (interventionReasonCodes: never[] & string[]) =>
                      interventionReasonCodes &&
                      interventionReasonCodes.length > 0 &&
                      !interventionReasonCodes.includes('RIR_0010'),
                  then: (schema) =>
                      schema.required(
                          'Please provide a comment for the intervention'
                      ),
                  otherwise: (schema) => schema.notRequired(),
              })
            : Yup.string(),
    });

const Rooms: React.FC<IInstallationRoomRouteProps> = ({ match }) => {
    const router = useCustomRouter();
    const jobId = +match.params.jobId;
    const roomId = match.params.roomId ? +match.params.roomId : undefined;
    const [presentToast] = useIonToast();
    const [presentActionSheet] = useIonActionSheet();
    const photosUploaded = usePhotosUploaded();
    const [, setPhotosUploaded] = useAtom(photosUploadedAtoms);
    const [isDeleting, setIsDeleting] = useState(false);
    const [accessToken] = useAtom(accessTokenAtom);
    const [value] = useAtom(installationWireRoomsLoadableAtom);
    const saveRooms = useSetAtom(roomsListLoadableAtom);
    const saveDevices = useSetAtom(devicesListLoadableAtom);
    const modal = useRef<HTMLIonModalElement>(null);
    const [openModal, setOpenModal] = useState(false);
    const [deepomatic, setDeepomatic] = useAtom(deepomaticAtom);
    const [, updateDBState] = useAtom(updateDBStateAtom);

    const room: IRoomWithDevices | undefined =
        value.state === 'hasData'
            ? value.data.rooms.find((r) => r.roomId === roomId)
            : undefined;
    const roomTypes =
        value.state === 'hasData' ? value.data.config.selects.roomTypes : [];
    const roomInsulationTypes =
        value.state === 'hasData'
            ? value.data.config.selects.roomInsulationTypes
            : [];
    const roomGlazingTypes =
        value.state === 'hasData'
            ? value.data.config.selects.roomGlazingTypes
            : [];
    const roomInterventionReasons = useMemo(
        () =>
            value.state === 'hasData'
                ? value.data.config.selects.roomInterventionReasons
                : [],
        [value]
    );

    const jobDetails =
        value.state === 'hasData' ? value.data.jobDetails : undefined;
    const isRepair = jobDetails?.typeInstallation === ODOO_INST_TYPE.REPAIR;
    const isNew = jobDetails?.typeInstallation === ODOO_INST_TYPE.NEW;
    const rooms = useMemo(
        () => (value.state === 'hasData' ? value.data.rooms : []),
        [value]
    );

    const updateUrl = useCallback(
        (roomId: number) => {
            router.push(
                `/${jobId}/installation/wire/rooms/${roomId}`,
                'none',
                'replace',
                undefined,
                NoAnimationBuilder
            );
        },
        [jobId, router]
    );

    const showSuccessMessage = useCallback(() => {
        presentToast({
            message: t`Room saved successfully`,
            duration: 5000,
            color: 'success',
            buttons: [
                {
                    text: t`Dismiss`,
                    role: 'cancel',
                },
            ],
        });
    }, [presentToast]);

    const deleteRoom = useCallback(async () => {
        setIsDeleting(true);

        try {
            const roomsToKeep = rooms.filter((r) => r.roomId !== roomId);
            const roomToDelete = rooms.filter((r) => r.roomId === roomId)[0];
            const devicesToKeep = roomsToKeep.reduce(
                (acc: THomeAssetData[], r) => [
                    ...acc,
                    ...r.devices.map((d) => ({
                        ...d,
                        roomId: r.roomId,
                    })),
                ],
                []
            );
            // check if room to be deleted has devices
            // and free up deepomatic slot if assigned
            let deepomaticCopy = [...deepomatic];
            roomToDelete.devices.forEach((d) => {
                const idx = deepomatic.findIndex((e) => e === d.macAddress);
                if (idx !== -1) {
                    deepomaticCopy[idx] = null;
                }
            });
            setDeepomatic(deepomaticCopy);
            //update db to reflect updated deepomatic array
            await updateDBState();

            if (!roomId) {
                throw new Error('Room ID is missing');
            }

            await deleteRoomRequest(accessToken, jobId, roomId);

            saveRooms(roomsToKeep);
            saveDevices(devicesToKeep);

            router.goBack();
        } catch (e: any) {
            const response = e.response;
            const errorMessage = response.rooms
                ? response.rooms.reduce((acc: string, message: string) => {
                      return `${acc}${message}<br/>`;
                  }, '')
                : e.reason;
            presentToast({
                message: errorMessage,
                duration: 5000,
                color: 'danger',
                buttons: [
                    {
                        text: t`Dismiss`,
                        role: 'cancel',
                    },
                ],
            });
        }

        setIsDeleting(false);
    }, [
        rooms,
        accessToken,
        jobId,
        saveRooms,
        saveDevices,
        router,
        roomId,
        presentToast,
        deepomatic,
        setDeepomatic,
        updateDBState,
    ]);

    const handleFormSubmit = useCallback(
        async (
            data: RoomsFormValues,
            actions: FormikHelpers<RoomsFormValues>
        ) => {
            // Data is still the same so avoid saving it again
            if (
                room &&
                room.name === data.name &&
                room.roomTypeId === data.type &&
                room.insulation === data.insulationType &&
                room.glazing === data.glazingType &&
                JSON.stringify(
                    room.interventionReasonCodes.map((el) => el.code).sort()
                ) === JSON.stringify(data.interventionReasonCodes.sort()) &&
                room.installerComment === data.installerComment
            ) {
                updateUrl(room.roomId);
                showSuccessMessage();
                return;
            }

            actions.setSubmitting(true);

            try {
                const createdUpdatedRoom = await createUpdateRoomRequest(
                    accessToken,
                    jobId,
                    {
                        name: data.name,
                        roomTypeId: data.type,
                        insulation: data.insulationType,
                        glazing: data.glazingType,
                        interventionReasonCodes:
                            data.interventionReasonCodes.map((code) => {
                                return {
                                    code,
                                    reason:
                                        roomInterventionReasons.find(
                                            (el) => el.value === code
                                        )?.label || '',
                                };
                            }),
                        installerComment: data.installerComment,
                    },
                    roomId
                );
                const roomsInCache = [
                    ...rooms.map((r) => {
                        const { devices, ...rest } = r;
                        return rest;
                    }),
                ];

                if (roomId) {
                    const index = roomsInCache.findIndex(
                        (r) => r.roomId === createdUpdatedRoom.roomId
                    );
                    roomsInCache[index] = createdUpdatedRoom;
                } else {
                    roomsInCache.push(createdUpdatedRoom);
                }

                saveRooms(roomsInCache);
                updateUrl(createdUpdatedRoom.roomId);
                showSuccessMessage();
            } catch (e: any) {
                const response = e.response;
                const errorMessage = response.name
                    ? response.name.reduce((acc: string, message: string) => {
                          return `${acc}${message}<br/>`;
                      }, '')
                    : e.reason;
                presentToast({
                    message: errorMessage,
                    duration: 5000,
                    color: 'danger',
                    buttons: [
                        {
                            text: t`Dismiss`,
                            role: 'cancel',
                        },
                    ],
                });
            }

            actions.setSubmitting(false);
        },
        [
            room,
            updateUrl,
            showSuccessMessage,
            accessToken,
            jobId,
            roomId,
            rooms,
            saveRooms,
            roomInterventionReasons,
            presentToast,
        ]
    );

    const handleDeleteRoom = useCallback(async () => {
        presentActionSheet({
            header: t`Delete Room`,
            subHeader: t`Are you sure you want to delete this room?`,
            buttons: [
                {
                    text: t`Delete`,
                    role: 'destructive',
                    data: {
                        action: 'delete',
                    },
                },
                {
                    text: t`Cancel`,
                    role: 'cancel',
                    data: {
                        action: 'cancel',
                    },
                },
            ],
            onWillDismiss: ({ detail }) => {
                if (detail.data.action === 'delete') {
                    deleteRoom();
                }
            },
        });
    }, [presentActionSheet, deleteRoom]);

    return (
        <InterventionPage title={t`Wire`}>
            <LoadableContent value={value}>
                <Formik
                    initialValues={{
                        name: room?.name || '',
                        type: room?.roomTypeId || 0,
                        insulationType: room?.insulation || '',
                        glazingType: room?.glazing || '',
                        interventionReasonCodes: room?.interventionReasonCodes
                            ? (room?.interventionReasonCodes.map(
                                  (el: { code: string; reason: string }) =>
                                      el.code
                              ) as string[])
                            : [],
                        installerComment: room?.installerComment || '',
                    }}
                    validationSchema={RoomsSchema(isRepair)}
                    validateOnMount={true}
                    onSubmit={handleFormSubmit}
                >
                    {({
                        values,
                        handleChange,
                        handleBlur,
                        errors,
                        touched,
                        isSubmitting,
                        isValid,
                    }) => (
                        <Form>
                            <div className="ion-padding">
                                {isRepair && (
                                    <IonCard
                                        color="warning"
                                        className="ion-no-margin"
                                    >
                                        <IonCardContent>
                                            <Trans>
                                                This is a repair installation.
                                                In case you need to uninstall
                                                and delete this room, please
                                                contact your supervisor.
                                                Deleting a room will cause data
                                                loss for the history of this
                                                room.
                                            </Trans>
                                        </IonCardContent>
                                    </IonCard>
                                )}
                                <IonCard
                                    className="ion-margin-top"
                                    onClick={() => setOpenModal(true)}
                                >
                                    <IonCardContent>
                                        <IonText>
                                            <IonIcon
                                                icon={hammerOutline}
                                                size="large"
                                                className="ion-float-left ion-padding-end"
                                            ></IonIcon>
                                            <Trans>
                                                Wiring instructions and
                                                diagrams for the D-Box v3
                                            </Trans>
                                        </IonText>
                                    </IonCardContent>
                                </IonCard>
                                <IonModal
                                    ref={modal}
                                    isOpen={openModal}
                                    backdropDismiss={false}
                                    onDidDismiss={() => {
                                        setOpenModal(false);
                                    }}
                                >
                                    <IonHeader>
                                        <IonToolbar>
                                            <IonButtons slot="end">
                                                <IonButton
                                                    onClick={() =>
                                                        modal.current?.dismiss()
                                                    }
                                                >
                                                    <Trans>Close</Trans>
                                                </IonButton>
                                            </IonButtons>
                                        </IonToolbar>
                                    </IonHeader>
                                    <IonContent className="ion-padding">
                                        <h4>
                                            <Trans>
                                                Check the role of each wire,
                                                then switch off the power, wire
                                                the D-Boxes in each room and
                                                take a photo of the wiring.
                                            </Trans>
                                            <IonImg
                                                className={
                                                    classes.wiringInstructionsImage
                                                }
                                                src="/assets/wiring_instructions.png"
                                                alt="Wiring instructions"
                                            />
                                        </h4>
                                    </IonContent>
                                </IonModal>
                                <div className="ion-margin-top ion-margin-bottom">
                                    <DefaultInput
                                        className={classNames({
                                            'ion-invalid': errors.name,
                                            'ion-touched': touched.name,
                                        })}
                                        name="name"
                                        value={values.name}
                                        label={t`Room name`}
                                        onIonInput={handleChange}
                                        errorText={errors.name}
                                    ></DefaultInput>
                                    <DefaultSelect
                                        className={classNames({
                                            'ion-invalid': errors.type,
                                            'ion-touched': touched.type,
                                        })}
                                        name="type"
                                        value={values.type}
                                        label={t`Room type`}
                                        placeholder={t`Select an option`}
                                        onIonChange={handleChange}
                                        onIonBlur={handleBlur}
                                        errorText={errors.type}
                                    >
                                        {roomTypes.map((roomType) => (
                                            <IonSelectOption
                                                key={roomType.value}
                                                value={roomType.value}
                                            >
                                                <TransServerMessage
                                                    id={roomType.label}
                                                />
                                            </IonSelectOption>
                                        ))}
                                    </DefaultSelect>
                                    <DefaultSelect
                                        className={classNames({
                                            'ion-invalid':
                                                errors.insulationType,
                                            'ion-touched':
                                                touched.insulationType,
                                        })}
                                        name="insulationType"
                                        value={values.insulationType}
                                        label={t`Are the walls of the room insulated?`}
                                        placeholder={t`Select an option`}
                                        onIonChange={handleChange}
                                        onIonBlur={handleBlur}
                                        helperText={t`Ask the tenant. If they are unsure, touch the wall with your hand. The walls are likely NOT insulated if they are cold in winter or warm in summer.`}
                                        errorText={errors.insulationType}
                                    >
                                        {roomInsulationTypes.map(
                                            (roomInsulationType) => (
                                                <IonSelectOption
                                                    key={
                                                        roomInsulationType.value
                                                    }
                                                    value={
                                                        roomInsulationType.value
                                                    }
                                                >
                                                    <TransServerMessage
                                                        id={
                                                            roomInsulationType.label
                                                        }
                                                    />
                                                </IonSelectOption>
                                            )
                                        )}
                                    </DefaultSelect>
                                    <DefaultSelect
                                        className={classNames({
                                            'ion-invalid': errors.glazingType,
                                            'ion-touched': touched.glazingType,
                                        })}
                                        name="glazingType"
                                        value={values.glazingType}
                                        label={t`What type of windows are in the room?`}
                                        placeholder={t`Select an option`}
                                        onIonChange={handleChange}
                                        onIonBlur={handleBlur}
                                        errorText={errors.glazingType}
                                    >
                                        {roomGlazingTypes.map(
                                            (roomGlazingType) => (
                                                <IonSelectOption
                                                    key={roomGlazingType.value}
                                                    value={
                                                        roomGlazingType.value
                                                    }
                                                >
                                                    <TransServerMessage
                                                        id={
                                                            roomGlazingType.label
                                                        }
                                                    />
                                                </IonSelectOption>
                                            )
                                        )}
                                    </DefaultSelect>
                                    {isRepair && (
                                        <>
                                            <DefaultSelect
                                                className={classNames({
                                                    'ion-invalid':
                                                        errors.interventionReasonCodes,
                                                    'ion-touched':
                                                        touched.interventionReasonCodes,
                                                })}
                                                name="interventionReasonCodes"
                                                value={
                                                    values.interventionReasonCodes
                                                }
                                                label={t`Reason/s for intervention`}
                                                placeholder={t`Select an option/s`}
                                                multiple={true}
                                                onIonChange={(e) => {
                                                    if (
                                                        e.detail.value
                                                            .length === 1 &&
                                                        e.detail.value.includes(
                                                            'RIR_0010'
                                                        )
                                                    ) {
                                                        setPhotosUploaded({});
                                                    }
                                                    handleChange(e);
                                                }}
                                                onIonBlur={handleBlur}
                                                helperText={t`You can select one or more reasons OR no intervention needed.`}
                                                errorText={
                                                    errors.interventionReasonCodes
                                                }
                                            >
                                                {roomInterventionReasons.map(
                                                    (
                                                        roomInterventionReason
                                                    ) => (
                                                        <IonSelectOption
                                                            key={
                                                                roomInterventionReason.value
                                                            }
                                                            value={
                                                                roomInterventionReason.value
                                                            }
                                                        >
                                                            <TransServerMessage
                                                                id={
                                                                    roomInterventionReason.label
                                                                }
                                                            />
                                                        </IonSelectOption>
                                                    )
                                                )}
                                            </DefaultSelect>
                                            {values.interventionReasonCodes
                                                .length > 0 &&
                                                !values.interventionReasonCodes.includes(
                                                    'RIR_0010'
                                                ) && (
                                                    <>
                                                        {
                                                            <PhotoUpload
                                                                className="ion-margin-top"
                                                                maxNumber={10}
                                                                imageCode={`${ImageCategories.roomInterventionBefore}_${roomId}`}
                                                                label={t`Photo of heater and surrounding area before intervention`}
                                                                mandatory={
                                                                    true
                                                                }
                                                            />
                                                        }
                                                        <DefaultTextarea
                                                            name="installerComment"
                                                            value={
                                                                values.installerComment
                                                            }
                                                            label={t`Comment on the repair (will appear in PVdR)`}
                                                            onIonInput={
                                                                handleChange
                                                            }
                                                        ></DefaultTextarea>
                                                    </>
                                                )}
                                        </>
                                    )}
                                </div>
                                {isNew && room && (
                                    <LoadingButton
                                        className="ion-margin-top"
                                        disabled={isSubmitting}
                                        loading={isDeleting}
                                        onClick={handleDeleteRoom}
                                    >
                                        <Trans>Delete Room</Trans>
                                    </LoadingButton>
                                )}
                                <LoadingButton
                                    className="ion-margin-top"
                                    disabled={
                                        !isValid ||
                                        isDeleting ||
                                        !photosUploaded
                                    }
                                    loading={isSubmitting}
                                    type="submit"
                                >
                                    <Trans>Save Room</Trans>
                                </LoadingButton>
                            </div>
                            {roomId && (
                                <>
                                    <IonList>
                                        <IonItemDivider color="primary">
                                            <IonLabel>
                                                <Trans>Devices</Trans>
                                            </IonLabel>
                                        </IonItemDivider>
                                        {room &&
                                            room.devices.map((device) => (
                                                <IonItem
                                                    key={device.id}
                                                    routerLink={`/${jobId}/installation/wire/rooms/${roomId}/devices/${device.id}`}
                                                    routerDirection="forward"
                                                >
                                                    <IonLabel>
                                                        {device.macAddress}
                                                    </IonLabel>
                                                </IonItem>
                                            ))}
                                    </IonList>
                                    <div className="ion-padding">
                                        <IonButton
                                            expand="block"
                                            disabled={
                                                isSubmitting || isDeleting
                                            }
                                            routerLink={`/${jobId}/installation/wire/rooms/${roomId}/devices`}
                                            routerDirection="forward"
                                        >
                                            <Trans>Add Device</Trans>
                                        </IonButton>
                                    </div>
                                </>
                            )}
                        </Form>
                    )}
                </Formik>
            </LoadableContent>
        </InterventionPage>
    );
};

export default Rooms;
